import React from 'react';

// Shared inline rich-text rendering for BOTH trusted admin content (Terms/About)
// and UNTRUSTED user-authored text (restaurant/food/menu descriptions).
//
// Supports inline **bold**, [text](url) markdown links, and autolinked bare
// http(s) URLs. All link hrefs pass through safeHref() so a hostile author
// cannot inject `javascript:`/`data:`/etc. — see #73.

const SAFE_SCHEMES = new Set(['http', 'https', 'mailto']);

// Autolink bare http(s) URLs. Stop at whitespace or a `<` (never mid-tag).
const BARE_URL = /(https?:\/\/[^\s<]+)/gi;

// Control/invisible chars browsers ignore *inside* a scheme, which would
// otherwise let "java\tscript:alert(1)" slip past the scheme check.
// eslint-disable-next-line no-control-regex
const INVISIBLE = /[\x00-\x20\x7f-\x9f\u200b-\u200d\ufeff]/g;

/**
 * Return a safe href, or null if the URL uses a disallowed scheme.
 * Relative/anchor URLs (no scheme) are treated as same-origin and allowed.
 */
export function safeHref(rawUrl) {
  const url = rawUrl.replace(INVISIBLE, '').trim();
  const scheme = url.match(/^([a-z][a-z0-9+.-]*):/i);
  if (!scheme) return url; // relative / anchor — same-origin navigation, safe
  return SAFE_SCHEMES.has(scheme[1].toLowerCase()) ? url : null;
}

function anchor(key, href, text) {
  // User content opens in a new tab; rel prevents tab-nabbing + referrer leak.
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );
}

// Turn bare http(s) URLs inside a plain-text run into links.
function linkifyPlain(text, keyPrefix) {
  const out = [];
  let last = 0;
  let i = 0;
  let m;
  BARE_URL.lastIndex = 0;
  while ((m = BARE_URL.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    let url = m[0];
    // Don't swallow trailing sentence punctuation into the URL.
    const trail = url.match(/[.,;:!?]+$/);
    let suffix = '';
    if (trail) {
      suffix = trail[0];
      url = url.slice(0, -suffix.length);
    }
    const href = safeHref(url);
    out.push(href ? anchor(`${keyPrefix}-a${i}`, href, url) : url);
    if (suffix) out.push(suffix);
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * Render one line/run of text into inline React nodes: **bold**, [text](url),
 * and autolinked bare URLs. Unsafe link schemes degrade to plain text.
 */
export function renderInline(text, keyPrefix) {
  // Split on bold and markdown links, keeping the delimiters.
  return text
    .split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g)
    .flatMap((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return [<strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>];
      }
      const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        const href = safeHref(link[2]);
        // Unsafe scheme → render the link *text* as plain, drop the href.
        return [href ? anchor(`${keyPrefix}-${i}`, href, link[1]) : link[1]];
      }
      return linkifyPlain(part, `${keyPrefix}-${i}`);
    });
}

/**
 * Inline-only rich text for free-text descriptions (prose). No block-level
 * headers/bullets — a casual author's leading `#`/`-` stays literal. Newlines
 * are preserved by the caller via CSS `white-space: pre-line`.
 */
export function renderRichText(text) {
  if (!text) return null;
  return renderInline(text, 'rt');
}
